import { IInquiry, IInquiryTarget } from '../interfaces/inquiry.interface';
import { IRecipient, IUser } from '../interfaces/user.interface';
import InquiryStore from '../store/InquiryStore';
import TravelStore from '../store/partner/TravelStore';
import UserStore from '../store/UserStore';

export interface RatParameter {
  componentId: string;
  customParameter: Record<string, string | number | boolean>;
}

export const defaultRatParameter: RatParameter = {
  componentId: '',
  customParameter: {},
};

const sleep = (msec: number) =>
  new Promise((resolve) => setTimeout(resolve, msec));

let initialized = true;
export function startRatTracking() {
  initialized = true;
}

function dispatch() {
  if (typeof (window as any).Event === 'function') {
    const event = new Event('SINGLE_PAGE_APPLICATION_LOAD');
    document.dispatchEvent(event);
  } else {
    try {
      const event = document.createEvent('Event');
      event.initEvent('SINGLE_PAGE_APPLICATION_LOAD', true, true);
      document.dispatchEvent(event);
    } catch (e) {
      console.log('RAT dispatch error', e);
    }
  }
}

function setVal(id: string, val: string) {
  const el = document.querySelector('input#' + id);
  if (el instanceof HTMLInputElement) {
    el.value = val;
  }
}

function camelToSnake(obj: Record<string, any>): Record<string, any> {
  const snakeObj: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`,
    );
    if (value !== null && typeof value === 'object') {
      snakeObj[snakeKey] = camelToSnake(value);
    } else {
      snakeObj[snakeKey] = value;
    }
  }
  return snakeObj;
}

async function sendPvEvent(
  ratPageLayout: string = '',
  ratSiteSection: string = '',
  ratPageName: string = '',
  ratShopId: string[] = [],
  ratCustomParameters: any = null,
) {
  // need to wait socket ? https://git.rakuten-it.com/projects/RMES/repos/ih-front/browse/src/store/RootStore.ts#57-61
  while (!initialized) {
    await sleep(1000);
  }
  ratPageLayout && setVal('ratPageLayout', ratPageLayout);
  ratSiteSection && setVal('ratSiteSection', ratSiteSection);
  ratPageName && setVal('ratPageName', ratPageName);
  ratShopId.length === 1 && setVal('ratShopId', ratShopId[0]);
  // ratShopIdList is for future expansion of multiple recipients. currently it is not supported!
  ratShopId.length > 1 && setVal('ratShopIdList', ratShopId.join(','));
  ratCustomParameters &&
    setVal(
      'ratCustomParameters',
      JSON.stringify(camelToSnake(ratCustomParameters)),
    );
  dispatch();
}

// @see https://confluence.rakuten-it.com/confluence/pages/viewpage.action?pageId=1990325579#SetupforWEBMeasurement-6.TrackingAnyAsynchronizedeventsbycustomcode
function sendRatNonPvEvent(
  componentId: string,
  eventType: RatEventTypeEnum,
  customParameters: Record<string, string | number | boolean> = {},
) {
  const customEvent = {
    // https://confluence.rakuten-it.com/confluence/display/MSP/0.RAT+Information
    pData: {
      compid: [componentId],
    },
    cpData: camelToSnake(customParameters),
    // Optionally, include page url and/or user agent
    options: ['url', 'ua'],
    includeInput: true,
    eventType,
  };

  // Finally, pass the custom event to RAT
  const RAT = (window as any).RAT;
  RAT && RAT.addCustomEvent(customEvent);
}

export enum RatEventTypeEnum {
  APPEAR = 'appear',
  CLICK = 'click',
}

export function handleRatPvEvent(
  userStore: UserStore,
  inquiryStore: InquiryStore,
  partnerStore: TravelStore,
) {
  try {
    if (inquiryStore.activeInquiry && partnerStore) {
      const activeInquiry = inquiryStore.activeInquiry;
      sendRatPvEvent(
        userStore.user,
        [
          {
            serviceId: partnerStore.SERVICE_ID,
            serviceAttribute: partnerStore.SERVICE_ATTRIBUTE,
            authId: activeInquiry.recipients[0].authId || '-1',
            userId: '',
            userIcon: '',
            userName: '',
          },
        ],
        activeInquiry,
        {
          targetId: activeInquiry.targetId,
          categoryId: activeInquiry.categoryId,
        },
      );
    } else {
      sendRatPvEvent(
        userStore.user,
        Array.from(userStore.recipients.values()),
        null,
        userStore.inquiryTarget,
      );
    }
  } catch (e) {
    console.error(e);
    // do nothing in RAT handling
  }
}

function sendRatPvEvent(
  user: IUser,
  recipients: IRecipient[],
  activeInquiry: IInquiry | null,
  inquiryTarget: IInquiryTarget,
) {
  const overwritten = {
    ...defaultRatParameter,
    customParameter: {
      serviceId: user.serviceId,
      serviceAttribute: user.serviceAttribute,
      categoryId: activeInquiry
        ? activeInquiry.categoryId
        : inquiryTarget.categoryId,
      inquiryCategoryId: activeInquiry?.inquiryCategory,
      inquirySubCategoryId: activeInquiry?.inquirySubCategory,
      targetId: inquiryTarget.targetId,
    },
  };

  // await is not needed
  sendPvEvent(
    'pc',
    'rmesse_platform',
    'rmesse_platform',
    Array.from(recipients.values()).map((e) => e.authId),
    overwritten.customParameter,
  );
}

export function sendRatClickEvent(overwriteParameter = {}) {
  const overwritten = { ...defaultRatParameter, ...overwriteParameter };
  sendRatNonPvEvent(
    overwritten.componentId,
    RatEventTypeEnum.CLICK,
    overwritten.customParameter,
  );
}
